import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Chip,
  CircularProgress,
  Alert,
  Box,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
  Stack,
  Avatar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { apiService } from '../../services/api';

const GarageAdminsModal = ({ open, onClose, garage }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    user_email: '',
    user_name: '',
    is_admin: false
  });

  useEffect(() => {
    if (open && garage) {
      setError(null);
      setSuccess(null);
      fetchAdmins();
    }
  }, [open, garage]);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getGarageAdmins(garage.id);
      setAdmins(response.data || []);
    } catch (error) {
      console.error('Error fetching admins:', error);
      setError(error.response?.data?.detail || 'Failed to fetch admins');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    try {
      setError(null);
      setSuccess(null);
      setLoading(true);
      await apiService.updateUser(userId, { 
        is_active: !currentStatus 
      });
      
      setAdmins(prevAdmins => 
        prevAdmins.map(admin => 
          admin.user_id === userId 
            ? { 
                ...admin, 
                is_active: !currentStatus
              }
            : admin
        )
      );
      
      setSuccess(`Admin ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error updating admin status:', error);
      setError(error.response?.data?.detail || 'Failed to update admin status');
    } finally {
      setLoading(false);
    }
  };

  const handlePrimaryToggle = async (adminId, currentPrimary) => {
    try {
      setError(null);
      setSuccess(null);
      setLoading(true);
      await apiService.updateGarageAdmin(garage.id, adminId, { 
        is_admin: !currentPrimary 
      });
      
      setAdmins(prevAdmins => 
        prevAdmins.map(admin => 
          admin.id === adminId 
            ? { ...admin, is_admin: !currentPrimary }
            : admin
        )
      );
      
      setSuccess(`Admin ${!currentPrimary ? 'set as primary' : 'removed as primary'} successfully`);
    } catch (error) {
      console.error('Error updating admin primary status:', error);
      setError(error.response?.data?.detail || 'Failed to update admin primary status');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdmin.user_email.trim() || !newAdmin.user_name.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setLoading(true);
      
      const adminData = {
        user_email: newAdmin.user_email,
        name: newAdmin.user_name,
        is_admin: newAdmin.is_admin
      };
      
      console.log('Sending admin data:', adminData);
      console.log('Garage ID:', garage.id);
      
      await apiService.createGarageAdmin(garage.id, adminData);
      
      setSuccess('Admin added successfully');
      setShowAddForm(false);
      setNewAdmin({ user_email: '', user_name: '', is_admin: false });
      await fetchAdmins();
    } catch (error) {
      console.error('Error adding admin:', error);
      setError(error.response?.data?.detail || 'Failed to add admin');
    } finally {
      setLoading(false);
    }
  };

  const renderMobileCard = (admin) => (
    <Paper key={admin.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
          <PersonIcon />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {admin.name || 'N/A'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {admin.email || 'N/A'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Chip
              label={admin.is_active ? 'Active' : 'Inactive'}
              color={admin.is_active ? 'success' : 'error'}
              size="small"
            />
            {admin.is_admin && (
              <Chip
                label="Primary"
                color="primary"
                size="small"
                icon={<AdminIcon />}
              />
            )}
          </Box>
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={admin.is_active}
                onChange={() => handleStatusToggle(admin.user_id, admin.is_active)}
                color="primary"
              />
            }
            label="Active"
          />
          <FormControlLabel
            control={
              <Switch
                checked={admin.is_admin}
                onChange={() => handlePrimaryToggle(admin.id, admin.is_admin)}
                color="primary"
              />
            }
            label="Primary"
          />
        </Box>
      </Box>
    </Paper>
  );

  const renderDesktopTable = () => (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Admin Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Primary</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(Array.isArray(admins) ? admins : []).map((admin) => (
            <TableRow key={admin.id}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                    <PersonIcon />
                  </Avatar>
                  <Typography variant="subtitle2">{admin.name}</Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{admin.email}</Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={admin.is_active ? 'Active' : 'Inactive'}
                    color={admin.is_active ? 'success' : 'error'}
                    size="small"
                  />
                  <Switch
                    checked={admin.is_active}
                    onChange={() => handleStatusToggle(admin.user_id, admin.is_active)}
                    color="primary"
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Switch
                    checked={admin.is_admin}
                    onChange={() => handlePrimaryToggle(admin.id, admin.is_admin)}
                    color="primary"
                  />
                  {admin.is_admin && (
                    <Chip
                      label="Primary"
                      color="primary"
                      size="small"
                      icon={<AdminIcon />}
                    />
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Manage Admins - {garage?.name}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}

        {!loading && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">
                Administrators ({admins.length})
              </Typography>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => setShowAddForm(!showAddForm)}
              >
                Add Admin
              </Button>
            </Box>

            {showAddForm && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box component="fieldset" sx={{ border: 'none', p: 0, m: 0 }}>
                  <Typography component="legend" variant="subtitle2" sx={{ mb: 3, fontWeight: 'bold' }}>
                    Add New Administrator
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 2
                  }}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={newAdmin.user_email}
                      onChange={(e) => setNewAdmin(prev => ({ ...prev, user_email: e.target.value }))}
                      placeholder="admin@garage.com"
                      InputProps={{
                        startAdornment: <EmailIcon sx={{ color: 'text.secondary', mr: 1 }} />
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={newAdmin.user_name}
                      onChange={(e) => setNewAdmin(prev => ({ ...prev, user_name: e.target.value }))}
                      placeholder="Admin Name"
                    />
                  </Box>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={newAdmin.is_admin}
                        onChange={(e) => setNewAdmin(prev => ({ ...prev, is_admin: e.target.checked }))}
                      />
                    }
                    label="Primary Admin"
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={handleAddAdmin}
                    disabled={loading}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setShowAddForm(false)}
                  >
                    Cancel
                  </Button>
                </Box>
                </Box>
              </Paper>
            )}

            {admins.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No administrators found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add an administrator to get started
                </Typography>
              </Box>
            ) : (
              isMobile ? (
                <Box>
                  {(Array.isArray(admins) ? admins : []).map(renderMobileCard)}
                </Box>
              ) : (
                renderDesktopTable()
              )
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GarageAdminsModal;